"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";

const UPPER_A = 0x41;
const UPPER_Z = 0x5a;
const LOWER_A = 0x61;
const LOWER_Z = 0x7a;

const FRAKTUR_UPPER_START = 0x1_d5_04;
const FRAKTUR_LOWER_START = 0x1_d5_1e;
const BOLD_FRAKTUR_UPPER_START = 0x1_d5_6c;
const BOLD_FRAKTUR_LOWER_START = 0x1_d5_86;

// Five Fraktur capitals predate the Mathematical Alphanumeric Symbols block,
// so their slots there are reserved and the Letterlike Symbols are used:
// C -> U+212D, H -> U+210C, I -> U+2111, R -> U+211C, Z -> U+2128.
const FRAKTUR_CAPITAL_EXCEPTIONS: Record<string, number> = {
  C: 0x21_2d,
  H: 0x21_0c,
  I: 0x21_11,
  R: 0x21_1c,
  Z: 0x21_28,
};

function mapLetter(
  char: string,
  upperStart: number,
  lowerStart: number,
  exceptions: Record<string, number> | null
): string {
  const code = char.codePointAt(0);
  if (code === undefined) {
    return char;
  }
  if (exceptions && char in exceptions) {
    return String.fromCodePoint(exceptions[char]);
  }
  if (code >= UPPER_A && code <= UPPER_Z) {
    return String.fromCodePoint(upperStart + (code - UPPER_A));
  }
  if (code >= LOWER_A && code <= LOWER_Z) {
    return String.fromCodePoint(lowerStart + (code - LOWER_A));
  }
  return char;
}

function toFraktur(text: string): string {
  return [...text]
    .map((char) =>
      mapLetter(
        char,
        FRAKTUR_UPPER_START,
        FRAKTUR_LOWER_START,
        FRAKTUR_CAPITAL_EXCEPTIONS
      )
    )
    .join("");
}

function toBoldFraktur(text: string): string {
  return [...text]
    .map((char) =>
      mapLetter(char, BOLD_FRAKTUR_UPPER_START, BOLD_FRAKTUR_LOWER_START, null)
    )
    .join("");
}

function ResultCard({
  copied,
  copiedLabel,
  copyLabel,
  emptyLabel,
  label,
  onCopy,
  value,
}: {
  copied: boolean;
  copiedLabel: string;
  copyLabel: string;
  emptyLabel: string;
  label: string;
  onCopy: () => void;
  value: string;
}) {
  return (
    <div className="space-y-2 rounded-lg border border-border bg-card p-5">
      <div className="flex items-center justify-between gap-3">
        <p className="font-medium text-foreground text-sm">{label}</p>
        <button
          className="rounded-lg border border-border bg-background px-4 py-1.5 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={value.length === 0}
          onClick={onCopy}
          type="button"
        >
          {copied ? copiedLabel : copyLabel}
        </button>
      </div>
      <p className="min-h-[2rem] whitespace-pre-wrap break-words text-base text-foreground leading-relaxed">
        {value.length > 0 ? value : emptyLabel}
      </p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const OLD_ENGLISH_TEXT_GENERATOR_STRINGS = {
  yourText: "Your text",
  inputPlaceholder: "Type or paste text to convert",
  oldEnglishFraktur: "Old English (Fraktur)",
  boldOldEnglishBoldFraktur: "Bold Old English (bold Fraktur)",
  copied: "Copied!",
  copy: "Copy",
  resultPlaceholder: "Your result will appear here",
  accessibilityHeading: "A note on accessibility",
  accessibilityBody:
    "Unicode Fraktur is not real formatting — it substitutes mathematical symbols that merely look like styled letters. Screen readers may announce each one as a mathematical symbol or skip it, and search and spell-check will not recognize the words. Use it for decoration, not for essential content.",
} as const;

export default function OldEnglishTextGeneratorWidget() {
  const [input, setInput] = useState("");
  const [copiedCard, setCopiedCard] = useState<string | null>(null);

  const strings = useToolUi(OLD_ENGLISH_TEXT_GENERATOR_STRINGS);

  const fraktur = toFraktur(input);
  const boldFraktur = toBoldFraktur(input);

  function handleCopy(card: string, value: string) {
    if (value.length === 0) {
      return;
    }
    writeToClipboard(value).then((copied) => {
      if (!copied) {
        return;
      }

      setCopiedCard(card);
      setTimeout(() => setCopiedCard(null), 2000);
    });
  }

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="old-english-input"
        >
          {strings.yourText}
        </label>
        <textarea
          className="min-h-[120px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="old-english-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
      </div>

      {/* Results */}
      <section className="space-y-4">
        <ResultCard
          copied={copiedCard === "fraktur"}
          copiedLabel={strings.copied}
          copyLabel={strings.copy}
          emptyLabel={strings.resultPlaceholder}
          label={strings.oldEnglishFraktur}
          onCopy={() => handleCopy("fraktur", fraktur)}
          value={input.length > 0 ? fraktur : ""}
        />
        <ResultCard
          copied={copiedCard === "bold"}
          copiedLabel={strings.copied}
          copyLabel={strings.copy}
          emptyLabel={strings.resultPlaceholder}
          label={strings.boldOldEnglishBoldFraktur}
          onCopy={() => handleCopy("bold", boldFraktur)}
          value={input.length > 0 ? boldFraktur : ""}
        />
      </section>

      {/* Accessibility caveat */}
      <section className="rounded-lg border border-border bg-muted/30 p-5">
        <h2 className="font-semibold text-base text-foreground">
          {strings.accessibilityHeading}
        </h2>
        <p className="mt-2 text-muted-foreground text-sm leading-relaxed">
          {strings.accessibilityBody}
        </p>
      </section>
    </>
  );
}
